import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import './SeasonalityCoefficientsPopup.css';

const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const SeasonalityCoefficientsPopup = ({
  marketplaceName,
  isOpen,
  onClose,
  loading,
  saving,
  error,
  successMessage,
  niches,
  updateCoefficient,
  applyBulkCoefficients,
  addNiche,
  saveChanges,
  cancelChanges,
}) => {
  const fileInputRef = useRef(null);
  const [fileMessage, setFileMessage] = useState('');
  const [newNicheName, setNewNicheName] = useState('');
  const [addingNiche, setAddingNiche] = useState(false);

  const handleAddNiche = async () => {
    if (!newNicheName.trim() || !addNiche) return;
    setAddingNiche(true);
    const ok = await addNiche(newNicheName);
    setAddingNiche(false);
    if (ok) setNewNicheName('');
  };

  // >1 → green tint (high season), <1 → red tint (low season)
  const coeffClass = (value) => {
    const n = parseFloat(value);
    if (isNaN(n) || n === 1) return '';
    return n > 1 ? ' coeff-hi' : ' coeff-lo';
  };

  // Template: Niche ID | Niche | JAN..DEC prefilled with current values
  const downloadTemplate = () => {
    const aoa = [
      ['Niche ID', 'Niche', ...months],
      ...niches.map(n => [
        n.niche_category_id,
        n.niche_name,
        ...n.coefficients.map(c => (isNaN(parseFloat(c)) ? 1 : parseFloat(c))),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 8 }, { wch: 28 }, ...months.map(() => ({ wch: 6 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Seasonality');
    XLSX.writeFile(wb, 'seasonality_coefficients.xlsx');
  };

  // Upload: match rows by Niche ID (col A), fallback by niche name (col B)
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileMessage('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: '' });
        const nameToId = {};
        niches.forEach(n => { nameToId[String(n.niche_name).trim().toLowerCase()] = n.niche_category_id; });
        const knownIds = new Set(niches.map(n => n.niche_category_id));
        const map = {};
        let matched = 0, skipped = 0;
        rows.slice(1).forEach(r => {
          let id = parseInt(r[0], 10);
          if (!knownIds.has(id)) id = nameToId[String(r[1]).trim().toLowerCase()];
          if (!id) { if (String(r[0]).trim() || String(r[1]).trim()) skipped++; return; }
          map[id] = r.slice(2, 14);
          matched++;
        });
        if (matched === 0) {
          setFileMessage('⚠ No niches matched — use the downloaded template format');
        } else {
          applyBulkCoefficients(map);
          setFileMessage(`Loaded ${matched} niche${matched !== 1 ? 's' : ''} from file${skipped ? `, ${skipped} rows not matched` : ''} — review and press "Save All Changes"`);
        }
      } catch (err) {
        setFileMessage(`⚠ Parse error: ${err.message}`);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsArrayBuffer(file);
  };

  if (!isOpen) return null;

  return (
    <div className="seasonality-popup-overlay">
      <div className="seasonality-popup-content">
        <div className="seasonality-popup-header">
          <div>
            <h2>Edit Seasonality Coefficients{marketplaceName ? ` — ${marketplaceName}` : ''}</h2>
            <p className="seasonality-popup-subtitle">
              Monthly multipliers per niche used by the YoY monthly forecast · green = high season, red = low
            </p>
          </div>
          <button className="close-popup-btn" onClick={onClose}>✕</button>
        </div>

        {loading && <div className="seasonality-loading">Loading coefficients...</div>}
        {error && <div className="seasonality-error">Error: {error}</div>}

        {!loading && !error && niches.length === 0 && (
          <div className="seasonality-empty">No niches found.</div>
        )}

        {!loading && !error && niches.length > 0 && (
          <>
            {/* File actions: template + bulk upload + add niche */}
            <div className="seasonality-file-actions">
              <button type="button" className="seasonality-file-btn outline" onClick={downloadTemplate}>
                <i className="fas fa-file-download"></i> Download Template
              </button>
              <button type="button" className="seasonality-file-btn solid"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                <i className="fas fa-file-upload"></i> Upload File
              </button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
              {fileMessage && (
                <span className={`seasonality-file-message${fileMessage.startsWith('⚠') ? ' warn' : ''}`}>{fileMessage}</span>
              )}

              <div className="seasonality-add-niche">
                <input
                  value={newNicheName}
                  onChange={e => setNewNicheName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddNiche(); }}
                  placeholder="New niche name…"
                  className="seasonality-add-niche-input"
                />
                <button
                  type="button"
                  className="seasonality-file-btn outline"
                  onClick={handleAddNiche}
                  disabled={!newNicheName.trim() || addingNiche}
                >
                  <i className={`fas fa-${addingNiche ? 'circle-notch fa-spin' : 'plus'}`}></i> Add Niche
                </button>
              </div>
            </div>

            <div className="seasonality-table-container">
              <table className="seasonality-table">
                <thead>
                  <tr>
                    <th>Niche</th>
                    {months.map(month => (
                      <th key={month}>{month}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {niches.map(niche => (
                    <tr key={niche.niche_category_id}>
                      <td className="niche-name">{niche.niche_name}</td>
                      {niche.coefficients.map((coeff, idx) => (
                        <td key={idx}>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={coeff}
                                onChange={(e) =>
                                updateCoefficient(niche.niche_category_id, idx, e.target.value)
                                }
                                className={`seasonality-input${coeffClass(coeff)}`}
                            />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Status messages – shown under the table */}
            <div className="seasonality-status-messages">
              {error && <div className="seasonality-error">Error: {error}</div>}
              {successMessage && <div className="seasonality-success">{successMessage}</div>}
            </div>

            <div className="seasonality-popup-footer">
              <button
                className="seasonality-btn cancel"
                onClick={() => {
                  cancelChanges();
                  onClose();
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="seasonality-btn save"
                onClick={async () => {
                  await saveChanges();
                  // optionally close after save? keep open to see result
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SeasonalityCoefficientsPopup;